import { apiClient } from "@api/client"

import type { School, SchoolDetail, SchoolOption, Subject } from "./types"

import type { GradeScale, PeriodType } from "@/constants/academic"
import type { EducationLevel } from "@/constants/education"
import type { EvaluationCalendar, EvaluationPeriodIn, ListPage } from "@/types"

export interface ListSchoolsParams {
  /** Omitted: every manageable institution (a superuser sees them all). */
  institutionId?: string
  view?: "all" | "trash"
  q?: string
  sort?: string
  page?: number
  pageSize?: number
}

export interface CreateSchoolPayload {
  institution_id: string
  name: string
  city?: string
  state?: string
  cct?: string
  // PRD-47 v2 — omitted resolves to "highschool" server-side.
  education_level?: EducationLevel
  // PRD-38 §FR-11 — nullable overrides; null (or omitted) = inherit.
  period_type?: PeriodType | null
  periods_per_cycle?: number | null
  grade_scale?: GradeScale | null
}

export interface UpdateSchoolPayload {
  name?: string
  city?: string
  state?: string
  cct?: string
  // PRD-47 v2 — non-null on the wire; the backend cascades the change to
  // the campus's classrooms and audits every rewritten row.
  education_level?: EducationLevel
  // PRD-38 §FR-11 — nullable overrides, and the null MATTERS. The backend
  // PATCH applies `exclude_unset=True`, so an ABSENT key leaves the stored
  // override untouched while an EXPLICIT null clears it back to "inherit".
  // The School forms therefore always send all three (see
  // `@lib/academicConfig::toAcademicOverridePayload`) — omitting them would
  // make "clear this override" impossible to express.
  period_type?: PeriodType | null
  periods_per_cycle?: number | null
  grade_scale?: GradeScale | null
}

// PRD-55 — logo upload. The first multipart call in this app; the pattern
// is ported from apps/frontend/src/api/students.ts::importRoster (FormData
// + a per-call Content-Type override + a longer timeout than the default
// 15s, since a 256 KB body on a school's connection can outlast it).
const LOGO_UPLOAD_TIMEOUT_MS = 60_000

function logoFormData(file: File): FormData {
  const form = new FormData()
  form.append("file", file)
  return form
}

export const schoolsApi = {
  get: (id: string) => apiClient.get<SchoolDetail>(`/admin/schools/${id}`).then((r) => r.data),

  // PRD-56 §FR-19 — a page, with per-view counts. The trash is `view=trash`
  // (only soft-deleted rows); it used to return live and deleted mixed.
  list: ({ institutionId, view, q, sort, page, pageSize }: ListSchoolsParams = {}) =>
    apiClient
      .get<ListPage<School>>("/admin/schools/", {
        params: {
          ...(institutionId ? { institution_id: institutionId } : {}),
          ...(view === "trash" ? { view } : {}),
          ...(q ? { q } : {}),
          ...(sort ? { sort } : {}),
          ...(page && page > 1 ? { page } : {}),
          ...(pageSize ? { page_size: pageSize } : {}),
        },
      })
      .then((r) => r.data),

  options: (institutionId: string) =>
    apiClient
      .get<SchoolOption[]>("/admin/schools/options", { params: { institution_id: institutionId } })
      .then((r) => r.data),

  create: (data: CreateSchoolPayload) =>
    apiClient.post<School>("/admin/schools/", data).then((r) => r.data),

  update: (id: string, data: UpdateSchoolPayload) =>
    apiClient.patch<School>(`/admin/schools/${id}`, data).then((r) => r.data),

  remove: (id: string) => apiClient.delete(`/admin/schools/${id}`).then((r) => r.data),

  restore: (id: string) =>
    apiClient.post<School>(`/admin/schools/${id}/restore`).then((r) => r.data),

  /** Replace this campus's crest, diverging from its institution. */
  uploadLogo: (id: string, file: File) =>
    apiClient
      .post<School>(`/admin/schools/${id}/logo`, logoFormData(file), {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: LOGO_UPLOAD_TIMEOUT_MS,
      })
      .then((r) => r.data),

  /** Clear the override, restoring inheritance from the institution. */
  removeLogo: (id: string) =>
    apiClient.delete<School>(`/admin/schools/${id}/logo`).then((r) => r.data),

  // PRD-61 FR-1.2 — the campus's EFFECTIVE calendar: its own rows, or its
  // institution's with `inherited: true`. No year = the one in course (see
  // institutionsApi.calendar). PUT sets its own set for the year; DELETE
  // drops it, so the campus inherits again. All three answer with the
  // effective calendar.
  calendar: (id: string, schoolYear: string | null) =>
    apiClient
      .get<EvaluationCalendar>(`/admin/schools/${id}/evaluation-calendar`, {
        params: schoolYear ? { school_year: schoolYear } : {},
      })
      .then((r) => r.data),

  saveCalendar: (id: string, schoolYear: string, periods: EvaluationPeriodIn[]) =>
    apiClient
      .put<EvaluationCalendar>(
        `/admin/schools/${id}/evaluation-calendar`,
        { periods },
        { params: { school_year: schoolYear } },
      )
      .then((r) => r.data),

  resetCalendar: (id: string, schoolYear: string) =>
    apiClient
      .delete<EvaluationCalendar>(`/admin/schools/${id}/evaluation-calendar`, {
        params: { school_year: schoolYear },
      })
      .then((r) => r.data),
}

export interface CreateSubjectPayload {
  name: string
}

export interface UpdateSubjectPayload {
  name?: string
}

// Subject — nested under School (PRD-37 §FR-8..11). Rename/delete/restore
// address the subject directly (`/admin/subjects/{id}`); list/create/
// seed-defaults are scoped under the owning school.
export const subjectsApi = {
  list: (schoolId: string, includeDeleted = false) =>
    apiClient
      .get<Subject[]>(`/admin/schools/${schoolId}/subjects/`, {
        params: {
          ...(includeDeleted ? { include_deleted: true } : {}),
        },
      })
      .then((r) => r.data),

  seedDefaults: (schoolId: string) =>
    apiClient
      .post<Subject[]>(`/admin/schools/${schoolId}/subjects/seed-defaults`)
      .then((r) => r.data),

  create: (schoolId: string, data: CreateSubjectPayload) =>
    apiClient.post<Subject>(`/admin/schools/${schoolId}/subjects/`, data).then((r) => r.data),

  update: (subjectId: string, data: UpdateSubjectPayload) =>
    apiClient.patch<Subject>(`/admin/subjects/${subjectId}`, data).then((r) => r.data),

  remove: (subjectId: string) =>
    apiClient.delete(`/admin/subjects/${subjectId}`).then((r) => r.data),

  restore: (subjectId: string) =>
    apiClient.post<Subject>(`/admin/subjects/${subjectId}/restore`).then((r) => r.data),
}
